import {
	addEdge,
	applyEdgeChanges,
	applyNodeChanges,
	Background,
	type Connection,
	Controls,
	type Edge,
	type EdgeChange,
	MiniMap,
	type Node,
	type NodeChange,
	ReactFlow,
	ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
	AlertTriangle,
	ArrowLeft,
	CheckCircle2,
	Pause,
	Play,
	Save,
	Send,
	Trash2,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchJson, sendJson } from "../../api/rest";
import { ConfigForm } from "../../components/flows/ConfigForm";
import { configSummary, useCatalogue } from "../../components/flows/catalogue";
import { type FlowNodeData, nodeTypes } from "../../components/flows/FlowNode";
import { Palette } from "../../components/flows/Palette";
import type { DeclaredParam } from "../../components/flows/paramSchema";
import { initialValues, ParamField } from "../../components/flows/runParams";
import { ScheduleTab } from "../../components/flows/ScheduleTab";
import {
	btn,
	ErrorBanner,
	fmtRelative,
	input,
	Panel,
	Spinner,
	StatusChip,
} from "../../components/flows/ui";
import type {
	FlowDefinition,
	FlowDetail,
	FlowSettings,
	NodeDef,
	StartResult,
	ValidationResult,
} from "../../types/flows";

// The canvas is React Flow's document and the flow definition is the same
// thing with our configs attached, so the two are converted rather than kept in
// parallel: `toDefinition` is the only place that decides what gets saved.

type Tab = "canvas" | "runs" | "schedule" | "settings";

const TABS: Array<{ id: Tab; label: string }> = [
	{ id: "canvas", label: "Canvas" },
	{ id: "runs", label: "Runs" },
	{ id: "schedule", label: "Schedule" },
	{ id: "settings", label: "Settings" },
];

const newNodeId = () => `n${Math.random().toString(36).slice(2, 9)}`;

const toDefinition = (nodes: Node[], edges: Edge[]): FlowDefinition => ({
	nodes: nodes.map((node) => {
		const data = node.data as FlowNodeData;
		return {
			id: node.id,
			type: (node.data as { nodeType?: string }).nodeType as string,
			position: node.position,
			data: {
				label: data.label,
				config:
					(node.data as { config?: Record<string, unknown> }).config ?? {},
			},
		};
	}),
	edges: edges.map((edge) => ({
		id: edge.id,
		source: edge.source,
		sourceHandle: edge.sourceHandle ?? null,
		target: edge.target,
		targetHandle: edge.targetHandle ?? null,
	})),
	viewport: { x: 0, y: 0, zoom: 1 },
});

const Editor = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { catalogue, node: nodeDef, accentFor } = useCatalogue();

	const [flow, setFlow] = useState<FlowDetail | null>(null);
	const [tab, setTab] = useState<Tab>("canvas");
	const [nodes, setNodes] = useState<Node[]>([]);
	const [edges, setEdges] = useState<Edge[]>([]);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [validation, setValidation] = useState<ValidationResult | null>(null);
	const [dirty, setDirty] = useState(false);
	const [unpublished, setUnpublished] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [runDialog, setRunDialog] = useState(false);
	const [loading, setLoading] = useState(true);
	const canvasRef = useRef<HTMLDivElement>(null);

	const load = useCallback(async () => {
		if (!id) return;
		// `/flows/new` is a route rather than a mode: a flow has to exist before a
		// canvas can be saved to it, so the blank one is created and the URL
		// replaced with its id.
		if (id === "new") {
			try {
				const created = await sendJson<{ id: number }>("/flows", "POST", {
					name: "Untitled flow",
				});
				navigate(`/flows/${created.id}`, { replace: true });
			} catch (e) {
				setError(String(e));
				setLoading(false);
			}
			return;
		}
		try {
			const detail = await fetchJson<FlowDetail>(`/flows/${id}`);
			setFlow(detail);
			setValidation(detail.validation);
			setUnpublished(Boolean(detail.unpublishedChanges));
			setNodes(
				detail.definition.nodes.map((n) => ({
					id: n.id,
					type: "flowNode",
					position: n.position,
					data: {
						nodeType: n.type,
						label: n.data.label ?? n.type,
						config: n.data.config ?? {},
					},
				})),
			);
			setEdges(
				detail.definition.edges.map((e) => ({
					id: e.id,
					source: e.source,
					sourceHandle: e.sourceHandle ?? undefined,
					target: e.target,
					targetHandle: e.targetHandle ?? undefined,
				})),
			);
			setDirty(false);
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, [id, navigate]);

	useEffect(() => {
		load();
	}, [load]);

	// A canvas is easy to lose by reflex — a browser Back after twenty minutes of
	// wiring. The router cannot intercept that, but the browser can.
	useEffect(() => {
		if (!dirty) return;
		const warn = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [dirty]);

	const issuesByNode = useMemo(() => {
		const map = new Map<string, string[]>();
		for (const issue of validation?.issues ?? []) {
			if (!issue.nodeId) continue;
			map.set(issue.nodeId, [...(map.get(issue.nodeId) ?? []), issue.message]);
		}
		return map;
	}, [validation]);

	/** React Flow nodes carry presentation; the definition carries the truth. */
	const renderNodes = useMemo<Node[]>(
		() =>
			nodes.map((node) => {
				const data = node.data as {
					nodeType: string;
					label: string;
					config: Record<string, unknown>;
				};
				const def = nodeDef(data.nodeType);
				return {
					...node,
					data: {
						...data,
						def,
						summary: configSummary(def, data.config),
						accent: accentFor(data.nodeType),
						issues: issuesByNode.get(node.id),
					} satisfies FlowNodeData & Record<string, unknown>,
				};
			}),
		[nodes, nodeDef, accentFor, issuesByNode],
	);

	const selected = nodes.find((n) => n.id === selectedId);
	const selectedDef = selected
		? nodeDef((selected.data as { nodeType: string }).nodeType)
		: undefined;

	const mutate = useCallback(() => {
		setDirty(true);
		setNotice(null);
	}, []);

	const onNodesChange = useCallback(
		(changes: NodeChange[]) => {
			setNodes((current) => applyNodeChanges(changes, current));
			if (changes.some((c) => c.type !== "select" && c.type !== "dimensions"))
				mutate();
		},
		[mutate],
	);

	const onEdgesChange = useCallback(
		(changes: EdgeChange[]) => {
			setEdges((current) => applyEdgeChanges(changes, current));
			if (changes.some((c) => c.type !== "select")) mutate();
		},
		[mutate],
	);

	const onConnect = useCallback(
		(connection: Connection) => {
			setEdges((current) =>
				addEdge({ ...connection, id: `e${newNodeId()}` }, current),
			);
			mutate();
		},
		[mutate],
	);

	const addNode = useCallback(
		(def: NodeDef, position?: { x: number; y: number }) => {
			const nodeId = newNodeId();
			const place =
				position ??
				(() => {
					const last = nodes[nodes.length - 1];
					return last
						? { x: last.position.x + 300, y: last.position.y }
						: { x: 80, y: 120 };
				})();

			setNodes((current) => [
				...current,
				{
					id: nodeId,
					type: "flowNode",
					position: place,
					data: { nodeType: def.type, label: def.label, config: {} },
				},
			]);

			// Clicking a palette entry means "next step", so it is wired to the
			// selected node if there is one — otherwise the editor would be a pile
			// of unconnected boxes.
			if (!position && selectedId) {
				setEdges((current) => [
					...current,
					{ id: `e${newNodeId()}`, source: selectedId, target: nodeId },
				]);
			}
			setSelectedId(nodeId);
			mutate();
		},
		[nodes, selectedId, mutate],
	);

	const updateSelected = useCallback(
		(patch: { label?: string; config?: Record<string, unknown> }) => {
			setNodes((current) =>
				current.map((node) =>
					node.id === selectedId
						? { ...node, data: { ...node.data, ...patch } }
						: node,
				),
			);
			mutate();
		},
		[selectedId, mutate],
	);

	const removeSelected = useCallback(() => {
		if (!selectedId) return;
		setNodes((current) => current.filter((n) => n.id !== selectedId));
		setEdges((current) =>
			current.filter((e) => e.source !== selectedId && e.target !== selectedId),
		);
		setSelectedId(null);
		mutate();
	}, [selectedId, mutate]);

	const save = useCallback(async () => {
		if (!id) return;
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<{
				ok: boolean;
				validation: ValidationResult;
				unpublishedChanges?: boolean;
			}>(`/flows/${id}`, "PUT", { definition: toDefinition(nodes, edges) });
			setValidation(result.validation);
			setUnpublished(Boolean(result.unpublishedChanges));
			setDirty(false);
			setNotice("Saved.");
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	}, [id, nodes, edges]);

	// Cmd/Ctrl+S, because that is what hands do.
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key === "s") {
				event.preventDefault();
				save();
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [save]);

	const validate = async () => {
		setBusy(true);
		setError(null);
		try {
			setValidation(
				await sendJson<ValidationResult>(`/flows/${id}/validate`, "POST"),
			);
			setNotice("Checked.");
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const setStatus = async (status: string) => {
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<{ ok?: boolean; error?: string }>(
				`/flows/${id}/status`,
				"POST",
				{ status },
			);
			if (result.error) setError(result.error);
			else await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	if (loading) return <Spinner />;
	if (!flow) return <ErrorBanner error={error ?? "No such flow."} />;

	const blocked = validation ? !validation.ok : false;

	return (
		<div className="flex h-[calc(100vh-8rem)] flex-col gap-3">
			<div className="flex flex-wrap items-center gap-2">
				<Link
					to="/flows"
					className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
					aria-label="Back to flows"
				>
					<ArrowLeft className="h-4 w-4" />
				</Link>
				<div className="min-w-0">
					<h1 className="truncate text-lg font-bold text-slate-800">
						{flow.name}
					</h1>
					<p className="flex items-center gap-1.5 text-[11px] text-slate-400">
						<StatusChip status={flow.status} />
						<span>v{flow.version}</span>
						{flow.settings.dryRun && <span>· dry run</span>}
						{dirty && (
							<span className="text-warning-dark">· unsaved changes</span>
						)}
						{!dirty && unpublished && (
							<span className="text-warning-dark">· saved, not published</span>
						)}
					</p>
				</div>

				<div className="ml-auto flex flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={validate}
						disabled={busy}
						className={btn.ghost}
					>
						<CheckCircle2 className="h-4 w-4" /> Validate
					</button>
					<button
						type="button"
						onClick={save}
						disabled={busy}
						className={btn.ghost}
					>
						<Save className="h-4 w-4" /> Save
					</button>
					<button
						type="button"
						onClick={() => setRunDialog(true)}
						disabled={busy}
						className={btn.ghost}
					>
						<Play className="h-4 w-4" /> Run now
					</button>
					{flow.status === "published" && unpublished && !dirty && (
						<button
							type="button"
							onClick={() => setStatus("published")}
							disabled={busy || blocked}
							title={
								blocked
									? "Fix the problems listed below before publishing"
									: `Runs use v${flow.version} until you publish these changes`
							}
							className={btn.primary}
						>
							<Send className="h-4 w-4" /> Publish changes
						</button>
					)}
					{flow.status === "published" ? (
						<button
							type="button"
							onClick={() => setStatus("paused")}
							disabled={busy}
							className={btn.ghost}
						>
							<Pause className="h-4 w-4" /> Pause
						</button>
					) : (
						<button
							type="button"
							onClick={() => setStatus("published")}
							disabled={busy || blocked}
							title={
								blocked
									? "Fix the problems listed below before publishing"
									: "Publish this flow"
							}
							className={btn.primary}
						>
							<Send className="h-4 w-4" /> Publish
						</button>
					)}
				</div>
			</div>

			<ErrorBanner error={error} />
			{flow.status !== "draft" && unpublished && !dirty && (
				<p className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
					The canvas has changes that are not live. Scheduled runs and Run now
					still use v{flow.version} — the parameters, prompts and settings as
					they were when it was published. Publish to make these changes the
					ones that run.
				</p>
			)}
			{notice && (
				<p className="rounded-lg bg-emerald-50 px-3 py-1.5 text-[12px] text-emerald-700">
					{notice}
				</p>
			)}

			{validation && validation.issues.length > 0 && (
				<div
					className={`rounded-xl border px-4 py-3 text-[12px] ${
						validation.ok
							? "border-amber-200 bg-amber-50 text-amber-800"
							: "border-danger/20 bg-danger/5 text-danger"
					}`}
				>
					<p className="flex items-center gap-1.5 font-medium">
						<AlertTriangle className="h-4 w-4" />
						{validation.ok
							? "Worth a look before this runs"
							: "This flow cannot be published until these are fixed"}
					</p>
					<ul className="mt-1.5 space-y-0.5">
						{validation.issues.map((issue) => (
							<li key={`${issue.nodeId}-${issue.message}`}>
								{issue.nodeId && validation.labels[issue.nodeId] && (
									<span className="font-medium">
										{validation.labels[issue.nodeId]}:{" "}
									</span>
								)}
								{issue.message}
							</li>
						))}
					</ul>
				</div>
			)}

			<div className="border-b border-slate-200">
				<nav className="flex gap-1">
					{TABS.map((entry) => (
						<button
							key={entry.id}
							type="button"
							onClick={() => setTab(entry.id)}
							className={`border-b-2 px-4 py-2 text-[13px] font-medium transition-all ${
								tab === entry.id
									? "border-primary text-primary"
									: "border-transparent text-slate-500 hover:text-slate-700"
							}`}
						>
							{entry.label}
						</button>
					))}
				</nav>
			</div>

			{tab === "canvas" && (
				<div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
					{catalogue && (
						<Palette catalogue={catalogue} onAppend={(def) => addNode(def)} />
					)}

					<div className="min-w-0 flex-1" ref={canvasRef}>
						<ReactFlow
							nodes={renderNodes}
							edges={edges}
							nodeTypes={nodeTypes}
							onNodesChange={onNodesChange}
							onEdgesChange={onEdgesChange}
							onConnect={onConnect}
							onNodeClick={(_, node) => setSelectedId(node.id)}
							onPaneClick={() => setSelectedId(null)}
							onDragOver={(event) => {
								event.preventDefault();
								event.dataTransfer.dropEffect = "move";
							}}
							onDrop={(event) => {
								event.preventDefault();
								const type = event.dataTransfer.getData(
									"application/flow-node",
								);
								const def = catalogue?.nodes.find((n) => n.type === type);
								if (!def || !canvasRef.current) return;
								const bounds = canvasRef.current.getBoundingClientRect();
								addNode(def, {
									x: event.clientX - bounds.left - 120,
									y: event.clientY - bounds.top - 30,
								});
							}}
							fitView
							proOptions={{ hideAttribution: true }}
						>
							<Background color="#e2e8f0" gap={18} />
							<Controls showInteractive={false} />
							<MiniMap pannable zoomable className="!bg-slate-50" />
						</ReactFlow>
					</div>

					{selected && selectedDef && (
						<div className="flex w-80 shrink-0 flex-col border-l border-slate-200 bg-white">
							<div className="border-b border-slate-200 p-3">
								<div className="flex items-start justify-between gap-2">
									<div className="min-w-0">
										<p className="truncate text-[13px] font-semibold text-slate-800">
											{selectedDef.label}
										</p>
										<p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
											{selectedDef.help.summary}
										</p>
									</div>
									<button
										type="button"
										onClick={() => setSelectedId(null)}
										className="rounded p-1 text-slate-400 hover:bg-slate-100"
										aria-label="Close"
									>
										<X className="h-4 w-4" />
									</button>
								</div>
							</div>

							<div className="flex-1 space-y-4 overflow-y-auto p-3">
								<label className="block space-y-1">
									<span className="block text-[12px] font-medium text-slate-600">
										Label
									</span>
									<input
										className={input}
										value={(selected.data as { label: string }).label}
										onChange={(event) =>
											updateSelected({ label: event.target.value })
										}
									/>
									<p className="text-[10px] text-slate-400">
										This is how other nodes address it: node("…").
									</p>
								</label>

								<ConfigForm
									def={selectedDef}
									config={
										(selected.data as { config?: Record<string, unknown> })
											.config ?? {}
									}
									reference={catalogue?.reference}
									upstreamLabels={nodes
										.filter((n) => n.id !== selectedId)
										.map((n) => (n.data as { label: string }).label)}
									flowId={flow?.id ?? null}
									nodeId={selectedId}
									onChange={(config) => updateSelected({ config })}
								/>
							</div>

							<div className="border-t border-slate-200 p-3">
								<button
									type="button"
									onClick={removeSelected}
									className={btn.danger}
								>
									<Trash2 className="h-4 w-4" /> Remove node
								</button>
							</div>
						</div>
					)}
				</div>
			)}

			{tab === "runs" && (
				<div className="overflow-y-auto">
					<Panel title="Recent runs" hint="the ten most recent">
						{flow.recentRuns.length === 0 ? (
							<p className="text-[13px] text-slate-400">
								This flow has never run.
							</p>
						) : (
							<table className="w-full text-left text-[13px]">
								<thead>
									<tr className="border-b border-slate-200 text-slate-500">
										<th className="pb-2 pr-3 font-medium">Run</th>
										<th className="pb-2 pr-3 font-medium">Trigger</th>
										<th className="pb-2 pr-3 font-medium">Status</th>
										<th className="pb-2 pr-3 font-medium">Started</th>
										<th className="pb-2 font-medium">Finished</th>
									</tr>
								</thead>
								<tbody>
									{flow.recentRuns.map((run) => (
										<tr
											key={run.id}
											className="border-b border-slate-100 last:border-0"
										>
											<td className="py-2 pr-3">
												<Link
													to={`/flows/runs/${run.id}`}
													className="font-medium text-primary"
												>
													#{run.id}
												</Link>
												{run.dryRun && (
													<span className="ml-1.5 text-[10px] text-slate-400">
														dry run
													</span>
												)}
											</td>
											<td className="py-2 pr-3 text-slate-500">
												{run.trigger}
											</td>
											<td className="py-2 pr-3">
												<StatusChip
													status={run.status}
													kind="run"
													title={run.error ?? undefined}
												/>
											</td>
											<td className="py-2 pr-3 text-slate-500">
												{fmtRelative(run.startedAt ?? run.createdAt)}
											</td>
											<td className="py-2 text-slate-500">
												{run.finishedAt ? fmtRelative(run.finishedAt) : "—"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</Panel>
				</div>
			)}

			{tab === "schedule" && (
				<div className="overflow-y-auto">
					<ScheduleTab
						flowId={flow.id}
						schedules={flow.schedules}
						humanTriggerOnly={validation?.humanTriggerOnly ?? false}
						onChanged={load}
					/>
				</div>
			)}

			{tab === "settings" && (
				<div className="overflow-y-auto">
					<FlowSettingsTab flow={flow} onSaved={load} />
				</div>
			)}

			{runDialog && (
				<RunDialog
					flow={flow}
					onClose={() => setRunDialog(false)}
					onStarted={(runId) => navigate(`/flows/runs/${runId}`)}
				/>
			)}
		</div>
	);
};

const RunDialog = ({
	flow,
	onClose,
	onStarted,
}: {
	flow: FlowDetail;
	onClose: () => void;
	onStarted: (runId: number) => void;
}) => {
	const trigger = flow.definition.nodes.find(
		(n) => n.type === "trigger.manual",
	);
	const declared =
		(trigger?.data.config as { params?: DeclaredParam[] })?.params ?? [];
	const [values, setValues] = useState<Record<string, string>>(() =>
		initialValues(declared),
	);
	const [dryRun, setDryRun] = useState(flow.settings.dryRun);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const start = async () => {
		setBusy(true);
		setError(null);
		try {
			const params: Record<string, unknown> = {};
			for (const param of declared) {
				const raw = values[param.key];
				if (raw === undefined) continue;
				// A boolean is sent even when false and a multiselect even when emptied, because
				// both are deliberate choices. Everything else keeps the old behaviour: an empty
				// box means "leave it to the node".
				if (param.type === "boolean") {
					params[param.key] = raw === "true";
					continue;
				}
				if (param.type === "multiselect") {
					params[param.key] = raw;
					continue;
				}
				if (raw === "") continue;
				params[param.key] = param.type === "number" ? Number(raw) : raw;
			}
			const result = await sendJson<StartResult & { error?: string }>(
				`/flows/${flow.id}/run`,
				"POST",
				{ params, dryRun },
			);
			if (result.runId) onStarted(result.runId);
			else setError(result.error ?? result.reason ?? "The run did not start.");
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
			<div className="w-full max-w-md space-y-4 rounded-2xl bg-white p-5 shadow-xl">
				<div className="flex items-start justify-between">
					<h3 className="text-[15px] font-semibold text-slate-800">
						Run {flow.name}
					</h3>
					<button
						type="button"
						onClick={onClose}
						className="rounded p-1 text-slate-400 hover:bg-slate-100"
						aria-label="Close"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				{declared.length === 0 ? (
					<p className="text-[12px] text-slate-500">
						This flow's trigger declares no parameters.
					</p>
				) : (
					<div className="space-y-3">
						{declared.map((param) => (
							<ParamField
								key={param.key}
								param={param}
								value={values[param.key] ?? ""}
								onChange={(next) => setValues({ ...values, [param.key]: next })}
							/>
						))}
					</div>
				)}

				<label className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-[12px] text-slate-600">
					<input
						type="checkbox"
						checked={dryRun}
						onChange={(event) => setDryRun(event.target.checked)}
						className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary"
					/>
					<span>
						<span className="font-medium text-slate-700">Dry run</span> — every
						node that would reach outside PriceOnline renders what it would have
						sent into the run inspector instead of sending it.
					</span>
				</label>

				<ErrorBanner error={error} />

				<div className="flex justify-end gap-2">
					<button type="button" onClick={onClose} className={btn.ghost}>
						Cancel
					</button>
					<button
						type="button"
						onClick={start}
						disabled={busy}
						className={btn.primary}
					>
						<Play className="h-4 w-4" /> Start
					</button>
				</div>
			</div>
		</div>
	);
};

const FlowSettingsTab = ({
	flow,
	onSaved,
}: {
	flow: FlowDetail;
	onSaved: () => void;
}) => {
	const [name, setName] = useState(flow.name);
	const [description, setDescription] = useState(flow.description ?? "");
	const [settings, setSettings] = useState<FlowSettings>(flow.settings);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			await sendJson(`/flows/${flow.id}`, "PUT", {
				name,
				description,
				settings,
			});
			onSaved();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Panel title="Flow settings">
			<div className="max-w-2xl space-y-4">
				<label className="block space-y-1">
					<span className="block text-[12px] font-medium text-slate-600">
						Name
					</span>
					<input
						className={input}
						value={name}
						onChange={(event) => setName(event.target.value)}
					/>
				</label>
				<label className="block space-y-1">
					<span className="block text-[12px] font-medium text-slate-600">
						Description
					</span>
					<textarea
						className={`${input} min-h-20`}
						value={description}
						onChange={(event) => setDescription(event.target.value)}
					/>
				</label>

				<label className="block space-y-1">
					<span className="block text-[12px] font-medium text-slate-600">
						Timeout (minutes)
					</span>
					<input
						type="number"
						min={1}
						className={`${input} w-32`}
						value={settings.timeoutMinutes}
						onChange={(event) =>
							setSettings({
								...settings,
								timeoutMinutes: Number(event.target.value),
							})
						}
					/>
				</label>

				<div className="space-y-3 rounded-xl border border-slate-200 p-4">
					<label className="flex items-start gap-2 text-[12px] text-slate-600">
						<input
							type="checkbox"
							checked={Boolean(settings.dryRun)}
							onChange={(event) =>
								setSettings({ ...settings, dryRun: event.target.checked })
							}
							className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary"
						/>
						<span>
							<span className="font-medium text-slate-700">Dry run</span> —
							outward nodes render what they would have sent instead of sending
							it. New flows start here on purpose.
						</span>
					</label>
					<label className="flex items-start gap-2 text-[12px] text-slate-600">
						<input
							type="checkbox"
							checked={Boolean(settings.notifySlack)}
							onChange={(event) =>
								setSettings({ ...settings, notifySlack: event.target.checked })
							}
							className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary"
						/>
						<span>
							<span className="font-medium text-slate-700">Notify Slack</span> —
							report each run, including failures.
						</span>
					</label>
					<label className="flex items-start gap-2 text-[12px] text-slate-600">
						<input
							type="checkbox"
							checked={Boolean(settings.skipIfRunning)}
							onChange={(event) =>
								setSettings({
									...settings,
									skipIfRunning: event.target.checked,
								})
							}
							className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary"
						/>
						<span>
							<span className="font-medium text-slate-700">
								Skip if already running
							</span>{" "}
							— a schedule that fires while the previous run is still going
							records a skipped run rather than starting a second one.
						</span>
					</label>
					<label className="flex items-start gap-2 text-[12px] text-slate-600">
						<input
							type="checkbox"
							checked={Boolean(settings.pressEnabled)}
							onChange={(event) =>
								setSettings({
									...settings,
									pressEnabled: event.target.checked,
								})
							}
							className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary"
						/>
						<span>
							<span className="font-medium text-slate-700">
								Send to the press list
							</span>{" "}
							— lets a Press list node in this flow mail the journalists in
							Settings. Off everywhere until you turn it on here, so a test run
							cannot reach a newsroom.
						</span>
					</label>
					<label className="flex items-start gap-2 text-[12px] text-slate-600">
						<input
							type="checkbox"
							checked={Boolean(settings.comparisonDrafting)}
							onChange={(event) =>
								setSettings({
									...settings,
									comparisonDrafting: event.target.checked,
								})
							}
							className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary"
						/>
						<span>
							<span className="font-medium text-slate-700">
								Comparison drafting
							</span>{" "}
							— marks this as the flow the Comparisons page's “Create pages”
							button starts. Exactly one published flow should have it; the
							button reports that nothing is marked when none does.
						</span>
					</label>
				</div>

				<ErrorBanner error={error} />

				<button
					type="button"
					onClick={save}
					disabled={busy}
					className={btn.primary}
				>
					<Save className="h-4 w-4" /> Save settings
				</button>
			</div>
		</Panel>
	);
};

const FlowEditor = () => (
	<ReactFlowProvider>
		<Editor />
	</ReactFlowProvider>
);

export default FlowEditor;
